import { DEFAULT_CURRENCY_CODE } from '../constants/currency.const';

/**
 * Symbols that replace the narrow one where the narrow one is ambiguous. Singapore dollars print
 * as a bare "$" otherwise, which reads as US dollars.
 */
const SYMBOL_OVERRIDES: Record<string, string> = { SGD: 'S$' };

const BARE_DOLLAR = '$';
const US_DOLLAR = 'USD';

const narrowFormatter = (code: string) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: code,
    currencyDisplay: 'narrowSymbol'
  });

/** The symbol a customer can't mistake: an override, else the narrow one unless it is a bare "$". */
const unambiguousSymbol = (code: string, narrow: string): string => {
  const override = SYMBOL_OVERRIDES[code];
  if (override) return override;
  if (narrow === BARE_DOLLAR && code !== US_DOLLAR) return code;
  return narrow;
};

const formatWith = (amount: number, code: string): string =>
  narrowFormatter(code)
    .formatToParts(amount)
    .map((part) =>
      part.type === 'currency'
        ? unambiguousSymbol(code, part.value)
        : part.value
    )
    .join('');

/**
 * Formats a monetary amount using Intl.NumberFormat, which handles symbol placement for the
 * given ISO 4217 code, with a symbol that names the currency unambiguously.
 *
 * Examples: formatCurrency(5, 'MYR') → 'RM5.00', formatCurrency(5, 'SGD') → 'S$5.00',
 * formatCurrency(5, 'PHP') → '₱5.00', formatCurrency(5, 'USD') → '$5.00'
 */
export function formatCurrency(
  amount: number,
  code: string = DEFAULT_CURRENCY_CODE
): string {
  try {
    return formatWith(amount, code);
  } catch {
    // Fallback for unrecognised code
    return formatWith(amount, DEFAULT_CURRENCY_CODE);
  }
}

/**
 * Returns just the currency symbol for the given ISO 4217 code, as `formatCurrency` prints it.
 *
 * Examples: getCurrencySymbol('MYR') → 'RM', getCurrencySymbol('SGD') → 'S$'
 */
export function getCurrencySymbol(
  code: string = DEFAULT_CURRENCY_CODE
): string {
  try {
    const narrow = narrowFormatter(code)
      .formatToParts(0)
      .find((p) => p.type === 'currency')?.value;
    return narrow ? unambiguousSymbol(code, narrow) : code;
  } catch {
    return DEFAULT_CURRENCY_CODE;
  }
}
